'use client';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentProps,
  type ReactNode,
} from 'react';

import { clsxm } from '@vs-shop/utils';

import { optionalClassName } from '../../util/optionalClassName/optionalClassName.ts';

import { clampedTextVariants, type ClampLines } from '../../typography/clampedText/ClampedText';
import { renderThrough, type RenderElement } from '../../util/render/render.tsx';

/**
 * A long block of copy cut down to a few lines, with a way to see the rest:
 *
 *   <CollapsibleBlock lines={3}>
 *     <CollapsibleBlockContent render={<Markdown>{description}</Markdown>} />
 *     <CollapsibleBlockToggle data-slot="collapsible-markdown-toggle">
 *       <CollapsibleBlockExpandLabel>Přečíst celé</CollapsibleBlockExpandLabel>
 *       <CollapsibleBlockCollapseLabel>Skrýt</CollapsibleBlockCollapseLabel>
 *     </CollapsibleBlockToggle>
 *   </CollapsibleBlock>
 *
 * **What the block owns is the clamp and the reveal; the copy is children.**
 * A category description, a brand's introduction and a CMS page's preamble are
 * three different renderers of the same shape, and none of them is this file's
 * business — `CollapsibleBlockContent` paints whatever element the composition
 * hands it, so a tenant's markdown renderer with its own plugin chain and its
 * own stylesheet stays entirely on the tenant's side.
 *
 * **The control appears only when there is something behind the clamp.** Text
 * that fits gets no "read the rest", because pressing it would do nothing
 * visible. That takes a MEASUREMENT — a clamp is a paint-time effect and no
 * amount of counting characters predicts it — which is why the state lives in
 * a context rather than in props: the control's existence is decided after the
 * content it is about has been laid out, and it is re-decided every time the
 * box changes width.
 *
 * The measurement runs before paint and again on every resize, so a shopper
 * rotating a phone does not end up with a control that reveals nothing or a
 * clamped block with no way past it.
 *
 * The two labels are parts rather than one string with a ternary at the call
 * site: which of them shows is this block's state, and a composition that had
 * to know it would need the state exposed — which is the escape hatch this
 * shape exists to avoid.
 */

type CollapsibleBlockState = {
  readonly isExpanded: boolean;
  /** Whether the content overflowed its clamp the last time it was measured. */
  readonly isClamped: boolean;
  /** False until the first measurement lands, so nothing flashes before it. */
  readonly isMeasured: boolean;
  readonly lines: ClampLines;
  readonly contentRef: (element: HTMLElement | null) => void;
  readonly toggle: () => void;
};

const CollapsibleBlockContext = createContext<CollapsibleBlockState | null>(null);

/**
 * The block's state, for the parts inside it.
 *
 * Throws rather than answering a default: a `CollapsibleBlockToggle` outside a
 * block would silently never appear, which reads as "the text fits" and is the
 * one failure this compound cannot afford to make quiet.
 */
const useCollapsibleBlock = (part: string): CollapsibleBlockState => {
  const state = useContext(CollapsibleBlockContext);

  if (state === null) {
    throw new Error(`${part} must be rendered inside a CollapsibleBlock.`);
  }

  return state;
};

export type CollapsibleBlockProps = ComponentProps<'div'> & {
  /** How many lines the collapsed block shows. */
  readonly lines: ClampLines;
  readonly children: ReactNode;
};

/** The clamped block. */
export const CollapsibleBlock = ({
  lines,
  className,
  children,
  ...props
}: CollapsibleBlockProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const [isMeasured, setIsMeasured] = useState(false);
  const elementRef = useRef<HTMLElement | null>(null);

  // A callback ref rather than an object one: the element this measures is
  // rendered by a part further down, possibly through `render`, and a callback
  // is the only form that survives being handed to an arbitrary element.
  const contentRef = useCallback((element: HTMLElement | null) => {
    elementRef.current = element;
  }, []);

  // What "clamped" means, in one place: the copy is taller than the box the
  // clamp gave it. Both the first measurement and every re-measurement ask it.
  const measure = useCallback((element: HTMLElement) => {
    setIsClamped(element.scrollHeight > element.clientHeight);
  }, []);

  // Measured before paint, so a block whose text fits never shows a control
  // that a first frame would then take away.
  useLayoutEffect(() => {
    const element = elementRef.current;

    if (element === null) {
      return;
    }

    measure(element);
    setIsMeasured(true);
  }, [children, lines, isExpanded, measure]);

  // And again whenever the box changes width: how much text fits in three
  // lines is a function of how wide those lines are.
  useEffect(() => {
    const element = elementRef.current;

    if (element === null) {
      return;
    }

    const observer = new ResizeObserver(() => {
      measure(element);
    });

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [children, lines, isExpanded, measure]);

  const toggle = useCallback(() => {
    setIsExpanded((expanded) => !expanded);
  }, []);

  return (
    <CollapsibleBlockContext
      value={{ isExpanded, isClamped, isMeasured, lines, contentRef, toggle }}
    >
      <div data-slot="collapsible-block" className={optionalClassName(clsxm(className))} {...props}>
        {children}
      </div>
    </CollapsibleBlockContext>
  );
};

export type CollapsibleBlockContentProps = ComponentProps<'div'> & {
  /**
   * The element that carries the copy — a tenant's markdown renderer, a prose
   * container. The clamp lands ON it rather than on a wrapper: `line-clamp`
   * counts the line boxes of the element it is set on, and a wrapper holding a
   * block child has none of its own to count.
   */
  readonly render?: RenderElement;
};

/** The copy, clamped while the block is collapsed. */
export const CollapsibleBlockContent = ({
  className,
  render,
  ...props
}: CollapsibleBlockContentProps) => {
  const { isExpanded, lines, contentRef } = useCollapsibleBlock('CollapsibleBlockContent');

  // The axis answers with nothing when it is not asked for, so revealing the
  // copy is one recipe call with no line count rather than a ternary over two
  // class strings (rule 08).
  const classes = clsxm(clampedTextVariants({ lines: isExpanded ? undefined : lines }), className);
  const withRef = { ...props, ref: contentRef };

  return (
    renderThrough(render, 'collapsible-block-content', classes, withRef) ?? (
      <div data-slot="collapsible-block-content" className={classes} {...withRef} />
    )
  );
};

/** The way past the clamp — its own line under the copy. */
const collapsibleBlockToggleClasses =
  'text-destructive lap:text-[16px]/6 mt-2 text-[15px]/6 whitespace-nowrap underline focus:outline-none';

export type CollapsibleBlockToggleProps = ComponentProps<'button'>;

/**
 * The reveal control.
 *
 * Renders nothing at all while the copy fits, and nothing before the first
 * measurement — an absent part yields no place, so the block simply ends at its
 * copy.
 */
export const CollapsibleBlockToggle = ({
  className,
  onClick,
  ...props
}: CollapsibleBlockToggleProps) => {
  const { isExpanded, isClamped, isMeasured, toggle } =
    useCollapsibleBlock('CollapsibleBlockToggle');

  if (!isMeasured || !(isClamped || isExpanded)) {
    return null;
  }

  return (
    <button
      type="button"
      data-slot="collapsible-block-toggle"
      aria-expanded={isExpanded}
      className={clsxm(collapsibleBlockToggleClasses, className)}
      onClick={(event) => {
        onClick?.(event);
        toggle();
      }}
      {...props}
    />
  );
};

export type CollapsibleBlockLabelProps = ComponentProps<'span'>;

/** What the control says while there is more to see. */
export const CollapsibleBlockExpandLabel = ({ ...props }: CollapsibleBlockLabelProps) => {
  const { isExpanded } = useCollapsibleBlock('CollapsibleBlockExpandLabel');

  return isExpanded ? null : <span data-slot="collapsible-block-expand-label" {...props} />;
};

/** What it says once there is not. */
export const CollapsibleBlockCollapseLabel = ({ ...props }: CollapsibleBlockLabelProps) => {
  const { isExpanded } = useCollapsibleBlock('CollapsibleBlockCollapseLabel');

  return isExpanded ? <span data-slot="collapsible-block-collapse-label" {...props} /> : null;
};
